import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Table } from 'primeng/table';
import { SmartintegService } from '../../../../services/smartinteg.service';
import { ToastService } from '../../../../services/toast.service';
import { mensagemErroApi } from '../../../../utils/mensagem-erro-api';
import { Processo, ExecutarProcessoResult } from '../../../../types/smartinteg';

@Component({
  selector: 'app-processo-list',
  templateUrl: './processo-list.component.html',
  styleUrls: ['./processo-list.component.css'],
  providers: [ConfirmationService]
})
export class ProcessoListComponent implements OnInit {

  @ViewChild('dt') table!: Table;

  processos: Processo[] = [];
  loading: boolean = false;
  executando: boolean = false;
  termoBusca = '';

  constructor(
    private smartintegService: SmartintegService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.carregarProcessos();
  }

  /**
   * Carregar lista de processos
   */
  carregarProcessos(): void {
    this.loading = true;
    this.smartintegService.listarProcessos().subscribe({
      next: (processos) => {
        this.processos = processos;
        this.loading = false;
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao carregar processos: ' + mensagemErroApi(error));
        this.loading = false;
      }
    });
  }

  /**
   * Navegar para criar novo processo
   */
  novoProcesso(): void {
    this.router.navigate(['/smartinteg/processos/novo']);
  }

  /**
   * Navegar para editar processo
   */
  editarProcesso(processo: Processo): void {
    this.router.navigate(['/smartinteg/processos/editar', processo.id]);
  }

  /**
   * Navegar para visualizar recursos do processo
   */
  visualizarRecursos(processo: Processo): void {
    this.router.navigate(['/smartinteg/processos', processo.id, 'fluxo']);
  }

  /**
   * Executar processo
   */
  executarProcesso(processo: Processo): void {
    this.confirmationService.confirm({
      message: `Deseja executar o processo <strong>${processo.nome}</strong>?`,
      header: 'Confirmar Execução',
      icon: 'pi pi-play',
      acceptButtonStyleClass: 'p-button-success',
      acceptLabel: 'Sim, Executar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.executando = true;
        this.smartintegService.executarProcesso(processo.id).subscribe({
          next: (resultado: ExecutarProcessoResult) => {
            if (resultado.sucesso) {
              this.toastService.sucesso('Sucesso', resultado.mensagem || 'Processo executado com sucesso');
            } else {
              this.toastService.alerta('Atenção', resultado.mensagem || 'Processo executado com avisos');
            }
            this.executando = false;
          },
          error: (error) => {
            this.toastService.erro('Erro', 'Erro ao executar processo: ' + mensagemErroApi(error));
            this.executando = false;
          }
        });
      }
    });
  }

  /**
   * Alternar status ativo/inativo do processo.
   * O backend alterna o estado em POST /processos/alterar-status/{id}; um PUT com "ativo" não tem efeito.
   */
  toggleAtivo(processo: Processo): void {
    const conhecido = processo.ativo !== undefined && processo.ativo !== null;
    const mensagem = conhecido
      ? `Deseja ${processo.ativo ? 'desativar' : 'ativar'} o processo <strong>${processo.nome}</strong>?`
      : `Deseja alternar o status (ativo/inativo) do processo <strong>${processo.nome}</strong>?`;

    this.confirmationService.confirm({
      message: mensagem,
      header: 'Confirmar Alteração',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sim',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.smartintegService.alterarStatusProcesso(processo.id).subscribe({
          next: () => {
            this.toastService.sucesso('Sucesso', 'Status do processo alterado com sucesso');
            this.carregarProcessos();
          },
          error: (error) => {
            this.toastService.erro('Erro', 'Erro ao alterar o status do processo: ' + mensagemErroApi(error));
          }
        });
      }
    });
  }

  /**
   * Deletar processo com confirmação. O backend não checa dependentes, então bloqueia se houver recursos.
   */
  deletarProcesso(processo: Processo): void {
    this.smartintegService.listarRecursosPorProcesso(processo.id).subscribe({
      next: (recursos) => {
        if (recursos.length > 0) {
          this.toastService.alerta(
            'Atenção',
            `O processo "${processo.nome}" possui ${recursos.length} recurso(s). Exclua os recursos antes de excluir o processo.`
          );
          return;
        }
        this.confirmarExclusao(processo);
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao verificar os recursos do processo: ' + mensagemErroApi(error));
      }
    });
  }

  private confirmarExclusao(processo: Processo): void {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja deletar o processo <strong>${processo.nome}</strong>?<br><br>Esta ação é irreversível.`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: 'Sim, Deletar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.loading = true;
        this.smartintegService.deletarProcesso(processo.id).subscribe({
          next: () => this.aposExcluir(processo),
          error: (error) => {
            this.toastService.erro('Erro', 'Erro ao deletar processo: ' + mensagemErroApi(error));
            this.loading = false;
          }
        });
      }
    });
  }

  // Se o backend confirma a exclusão mas o processo continua listado (pendência P2), avisa em vez de dizer "sucesso"
  private aposExcluir(processo: Processo): void {
    this.smartintegService.listarProcessos().subscribe({
      next: (processos) => {
        this.processos = processos;
        this.loading = false;
        if (processos.some(p => p.id === processo.id)) {
          this.toastService.alerta('Atenção', `O backend confirmou a exclusão, mas o processo "${processo.nome}" continua listado.`);
        } else {
          this.toastService.sucesso('Sucesso', 'Processo deletado com sucesso');
        }
      },
      error: (error) => {
        this.toastService.erro('Erro', 'Erro ao recarregar processos: ' + mensagemErroApi(error));
        this.loading = false;
      }
    });
  }

  /**
   * A API ainda não informa o estado (pendência P3): ativo fica undefined
   */
  getAtivoSeverity(ativo?: boolean): string {
    if (ativo === undefined || ativo === null) return 'secondary';
    return ativo ? 'success' : 'danger';
  }

  getAtivoLabel(ativo?: boolean): string {
    if (ativo === undefined || ativo === null) return 'Status desconhecido (a API ainda não informa). Clique para alternar.';
    return ativo ? 'Ativo' : 'Inativo';
  }

  getAtivoIcon(ativo?: boolean): string {
    if (ativo === undefined || ativo === null) return 'pi pi-question-circle';
    return ativo ? 'pi pi-check-circle' : 'pi pi-times-circle';
  }

  getAtivoStyleClass(ativo?: boolean): string {
    if (ativo === undefined || ativo === null) return 'p-button-secondary';
    return ativo ? 'p-button-success' : 'p-button-danger';
  }

  /**
   * Formatar data para exibição
   */
  formatarData(data: Date): string {
    if (!data) return '-';
    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Limpar filtros da tabela
   */
  limparFiltros(): void {
    this.termoBusca = '';
    this.table.clear();
  }
}
